// src/pages/ContentEditor/hooks/useContentEditorState.js
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { get, update, recreateCover } from '../../../../../services/practice-service';

const defaultFormState = {
  gradeLevel: 1,
  title: '',
  topicCategory: '',
  topic: '',
  fluencyWordCount: 100,
  synopsis: '',
  fluencyActivity: { paragraphs: [{ text: '' }] },
  vocabularyActivity: { sentences: [{ sentence: { text: '' } }] },
  decodeActivity: { words: [{ word: { text: '' } }] },
  comprehensionActivity: {
    questions: [{
      questionText: { text: '' },
      options: [{ text: '' }, { text: '' }, { text: '' }, { text: '' }],
      correctIndex: 0
    }]
  },
  thumbnailUrl: '',
};

export const useContentEditorState = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [formData, setFormData] = useState(defaultFormState);
  const [recreatingCover, setRecreatingCover] = useState(false);

  const fetchContentDetails = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await get(id);
      setContent(data);
    } catch (err) {
      setError('Failed to fetch content details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchContentDetails();
  }, [id, fetchContentDetails]);

  useEffect(() => {
    if (content) {
      const fluencyActivity = content.activities?.find(activity => activity.type === 'FLUENCY') || {
        content: { paragraphs: [{ raw: ''}] }
      };

      const vocabularyActivity = content.activities?.find(activity => activity.type === 'VOCABULARY') || {
        content: { sentences: [{ raw: '' }] }
      };

      const decodeActivity = content.activities?.find(activity => activity.type === 'DECODE') || {
        content: { words: [{ raw: '' }] }
      };

      const comprehensionActivity = content.activities?.find(activity => activity.type === 'COMPREHENSION') || {
        content: {
          questions: [{
            questionText: '',
            options: ['','','',''],
            correctIndex: 0
          }]
        }
      };

      setFormData({
        thumbnailUrl: content.cover.thumbnailUrl || '',
        gradeLevel: content.gradeLevel || 1,
        topicCategory: content.cover?.category || '',
        title: content.cover.title || '',
        synopsis: content.cover.synopsis || '',
        fluencyWordCount: content.fluencyWordCount || 100,
        fluencyActivity: {
          paragraphs: fluencyActivity.content?.paragraphs?.map(p => ({text: p.raw || ''})) || [{ text: '' }]
        },
        vocabularyActivity: {
          sentences: vocabularyActivity.content?.sentences?.map(s => ({
            sentence: { text: s.raw || '' }
          })) || [{ sentence: { text: '' } }]
        },
        decodeActivity: {
          words: decodeActivity.content?.words?.map(w => ({
            word: { text: w.raw || '' }
          })) || [{ word: { text: '' } }]
        },
        comprehensionActivity: {
          questions: comprehensionActivity.content?.questions?.map(q => ({
            questionText: { text: q.text || '' },
            options: q.options?.map(o => ({ text: o || '' })) ||
              [{ text: '' }, { text: '' }, { text: '' }, { text: '' }],
            correctIndex: q.correctIndex || 0
          })) || [{
            questionText: { text: '' },
            options: [{ text: '' }, { text: '' }, { text: '' }, { text: '' }],
            correctIndex: 0
          }]
        }
      });
    }
  }, [content]);


  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setUnsavedChanges(true);
  };

  const handleNestedChange = (section, index, field, value) => {
    setFormData(prev => {
      const prevSection = prev[section];
      let updatedSection;

      if (section === 'decodeActivity') {
        const words = prevSection.words.map((w, idx) => 
          idx === index 
            ? { ...w, word: { ...w.word, text: value } }
            : w
        );
        updatedSection = { ...prevSection, words };
      } else if (section === 'vocabularyActivity') {
        if (field === 'sentence') {
          const sentences = prevSection.sentences.map((s, idx) =>
            idx === index
              ? { ...s, sentence: { ...s.sentence, text: value } }
              : s
          );
          updatedSection = { ...prevSection, sentences };
        }
      } else if (section === 'fluencyActivity') {
        const paragraphs = prevSection.paragraphs.map((p, idx) =>
          idx === index
            ? { ...p, text: value }
            : p
        );
        updatedSection = { ...prevSection, paragraphs };
      } else if (section === 'comprehensionActivity') {
        const questions = prevSection.questions.map((q, idx) => {
          if (idx !== index) return q;

          if (field === 'question') {
            return {
              ...q,
              questionText: { ...q.questionText, text: value }
            };
          } else if (field.startsWith('option')) {
            const optionIndex = parseInt(field.split('-')[1]);
            const options = q.options.map((opt, oIdx) =>
              oIdx === optionIndex
                ? { ...opt, text: value }
                : opt
            );
            return { ...q, options };
          } else if (field === 'correctIndex') {
            return {
              ...q,
              correctIndex: parseInt(value)
            };
          }
          return q;
        });
        updatedSection = { ...prevSection, questions };
      }

      return {
        ...prev,
        [section]: updatedSection
      };
    });
    setUnsavedChanges(true);
  };

  const handleAddItem = (section) => {
    setFormData(prev => {
      const prevSection = prev[section];
      let updatedSection;

      if (section === 'decodeActivity') {
        updatedSection = {
          ...prevSection,
          words: [...prevSection.words, { word: { text: '' } }]
        };
      } else if (section === 'vocabularyActivity') {
        updatedSection = {
          ...prevSection,
          sentences: [...prevSection.sentences, { sentence: { text: '' } }]
        };
      } else if (section === 'fluencyActivity') {
        updatedSection = {
          ...prevSection,
          paragraphs: [...prevSection.paragraphs, { text: '' }]
        };
      } else if (section === 'comprehensionActivity') {
        updatedSection = {
          ...prevSection,
          questions: [
            ...prevSection.questions,
            {
              questionText: { text: '' },
              options: [{ text: '' }, { text: '' }, { text: '' }, { text: '' }],
              correctIndex: 0
            }
          ]
        };
      }

      return {
        ...prev,
        [section]: updatedSection
      };
    });
    setUnsavedChanges(true);
  };

  const handleRemoveItem = (section, index) => {
    setFormData(prev => {
      const prevSection = prev[section];
      let updatedSection;

      if (section === 'decodeActivity') {
        const words = prevSection.words.filter((_, idx) => idx !== index);
        updatedSection = {
          ...prevSection,
          words: words.length === 0 ? [{ word: { text: '' } }] : words
        };
      } else if (section === 'vocabularyActivity') {
        const sentences = prevSection.sentences.filter((_, idx) => idx !== index);
        updatedSection = {
          ...prevSection,
          sentences: sentences.length === 0 ? [{ sentence: { text: '' } }] : sentences
        };
      } else if (section === 'fluencyActivity') {
        const paragraphs = prevSection.paragraphs.filter((_, idx) => idx !== index);
        updatedSection = {
          ...prevSection,
          paragraphs: paragraphs.length === 0 ? [{ text: '' }] : paragraphs
        };
      } else if (section === 'comprehensionActivity') {
        const questions = prevSection.questions.filter((_, idx) => idx !== index);
        updatedSection = {
          ...prevSection,
          questions: questions.length === 0
            ? [{
                questionText: { text: '' },
                options: [{ text: '' }, { text: '' }, { text: '' }, { text: '' }],
                correctIndex: 0
              }]
            : questions
        };
      }

      return {
        ...prev,
        [section]: updatedSection
      };
    });
    setUnsavedChanges(true);
  };

  const handleSave = async () => {
    if (!formData.title?.trim()) {
      setError('Title is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updateData = {
        gradeLevel: formData.gradeLevel || 1,
        cover: {
          title: { raw: { value: formData.title || '' } },
          topic: { raw: { value: formData.topic || '' } },
          synopsis: { raw: { value: formData.synopsis || '' } },
          thumbnailImage: {
            url: formData.thumbnailUrl || ''
          }
        },
        activities: []
      };

      updateData.activities.push({
        type: 'DECODE',
        content: {
          words: formData.decodeActivity?.words?.map(w => ({
            raw: { value: w.word?.text || '' }
          })) || []
        }
      });

      updateData.activities.push({
        type: 'VOCABULARY',
        content: {
          sentences: formData.vocabularyActivity?.sentences?.map(s => ({
            raw: { value: s.sentence?.text || '' }
          })) || []
        }
      });

      updateData.activities.push({
        type: 'FLUENCY',
        content: {
          paragraphs: formData.fluencyActivity?.paragraphs?.map(p => ({
            raw: { value: p.text || '' }
          })) || []
        }
      });

      updateData.activities.push({
        type: 'COMPREHENSION',
        content: {
          questions: formData.comprehensionActivity?.questions?.map(q => ({
            questionText: { raw: { value: q.questionText?.text || '' } },
            options: q.options?.map(o => ({ raw: { value: o.text || '' } })) || [],
            correctIndex: q.correctIndex || 0
          })) || []
        }
      });

      try {
        await update(id, updateData);
        navigate(`/reading-practices/${type || 'draft'}/${id}`);
      } catch (apiError) {
        console.error("API error details:", apiError);
        setError(`${apiError.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error("Error saving content:", err);
      setError(`Failed to save content: ${err.message || 'Please try again.'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRecreateCover = async () => {
    setRecreatingCover(true);
    setError(null);
    try {
      const updatedData = await recreateCover(id);
      setFormData(prev => ({
        ...prev,
        thumbnailUrl: updatedData.cover?.thumbnailUrl || prev.thumbnailUrl
      }));
      setUnsavedChanges(true);
    } catch (err) {
      setError(`Failed to recreate cover: ${err.message || 'Please try again.'}`);
    } finally {
      setRecreatingCover(false);
    }
  };

  const handleCancel = () => {
    if (unsavedChanges) {
      setDiscardDialogOpen(true);
    } else {
      navigateBack();
    }
  };

  const navigateBack = () => {
    navigate(`/reading-practices/${type || 'draft'}/${id}`);
  };

  const handleDiscardConfirm = () => {
    setDiscardDialogOpen(false);
    navigateBack();
  };

  const handleDiscardCancel = () => {
    setDiscardDialogOpen(false);
  };

  return {
    content,
    formData,
    loading,
    saving,
    error,
    tabValue,
    unsavedChanges,
    discardDialogOpen,
    setContent,
    setFormData,
    setLoading,
    setSaving,
    setError,
    setTabValue,
    setUnsavedChanges,
    setDiscardDialogOpen,
    handleTabChange,
    handleInputChange,
    handleNestedChange,
    handleAddItem,
    handleRemoveItem,
    handleSave,
    handleCancel,
    handleDiscardConfirm,
    handleDiscardCancel,
    navigateBack,
    fetchContentDetails,
    recreatingCover,
    handleRecreateCover
  };
};
